import { Component, OnInit, OnDestroy, Input} from '@angular/core';
import { CategoryScore, CategorySingle, GaitReportDiagrams, RunningAnalysisReportViewModel, StringViewModel } from 'src/app/_models/generatedModels';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentService } from 'src/app/_services/generatedServices';
import { SettingsProvider } from 'src/app/_services/settingsProvider.service';
import { ToasterService } from 'src/app/_services/toaster.service';
import { Enums } from '../../_models/generatedEnums';
import { isLatestProtocol } from '../../_helpers/protocols';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RunningReportVideoPopupComponent } from './running-report-video-popup.component';
import { RunningReportDiagramPopupComponent } from './running-report-diagram-popup.component';
import { ExerciseToolTipModalComponent } from '../../coaches-admin/Clients/assessments/exercise-tool-tip-modal.component';
import { AuthenticationService } from '../../_services/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'running-analysis-result-details',
  templateUrl: 'running-analysis-result-details.component.html',
  styleUrls: ['running-analysis-result-details.component.scss']
})

export class RunningAnalysisResultDetailsComponent implements OnInit, OnDestroy
{
  initialized = false;
  analysisId: any;
  analysis: RunningAnalysisReportViewModel;
  shareableLink: string;
  categoryScoreEnum = Enums.CategoryScoreEnum;
  speedUnitEnum = Enums.SpeedUnitEnum;
  runningAnalysisProgramPrimaryFactorEnum = Enums.RunningAnalysisProgramPrimaryFactorEnum;
  impairmentCategoryEnum = Enums.ImpairmentCategoryEnum;
  AssessmentResultShortEnum = Enums.AssessmentResultShortEnum;
  runningAnalysisReportScoreDescriptionEnum = Enums.RunningAnalysisReportScoreDescriptionEnum;
  footStrikeEnum = Enums.FootStrikePatternEnum;
  isLatestProtocol = isLatestProtocol;
  programUrl: string = '/library/programs/view';

  gscProgramDescription: string;
  groupedDiagrams: any;

  @Input()
  isCoachView: boolean = false;

  @Input()
  isShared: boolean = false;

  constructor(private route: ActivatedRoute, private router: Router, private assessmentData: AssessmentService, private settingsProvider: SettingsProvider, private toastr: ToasterService, private auth: AuthenticationService, private modalService: NgbModal) { }

  ngOnInit(): void
  {
    this.analysisId = this.route.snapshot.params ? this.route.snapshot.params['id'] : null;

    let pattern = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    let isGuid = this.analysisId.match(pattern) ? true : false;

    if (isGuid && this.isShared) {
      const model = new StringViewModel();
      model.value = this.analysisId;
      this.assessmentData.getSpecificRunningReportByGuid(model).subscribe((result) =>
      {
        this.analysis = result;
        this.analysis.guidId
        this.groupedDiagrams = this.groupBy(this.analysis.diagrams, 'category');
        this.initialized = true;
      });
    } else {
      this.assessmentData.getSpecificRunningReport(this.analysisId).subscribe(
        (result) => {
          this.analysis = result;
          this.shareableLink = this.settingsProvider.siteUrl + '/gait-running/' + this.analysis.guidId;
          this.groupedDiagrams = this.groupBy(this.analysis.diagrams, 'category');
          this.initialized = true;
        },
        (err: HttpErrorResponse) =>
        {
          if (err && err.error && err.error.indexOf('UnauthorizedAccessException') >= 0) {
            this.toastr.error('You do not have access to the assessment', 'Error',);
          } else {
            this.toastr.error('An error occurred while loading the assessment', 'Error');
          }

          this.router.navigate(['../../../'], { relativeTo: this.route });

        });
    }
  }

  onCopyLink()
  {
    //copy link to clipboard
    let link = document.createElement('textarea');
    link.value = this.shareableLink;
    document.body.appendChild(link);
    link.select();
    document.execCommand('copy');
    document.body.removeChild(link);
    this.toastr.success('Link copied to clipboard', 'Success');
  }

  htmlToText(html: string)
  {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  showVideo()
  {
    const modalRef = this.modalService.open(RunningReportVideoPopupComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.analysisName = this.analysis.name;
    modalRef.componentInstance.videoLink = this.analysis.assetPath;
    modalRef.componentInstance.encoded = this.analysis.encoded;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  getGradientPosition(minValue, maxValue, averageValue, standardDeviation, stDeviationsCount)
  {
    // Calculate the value based on the number of standard deviations
    const value = averageValue + stDeviationsCount * standardDeviation;

    // Ensure the value doesn't go below the minValue or above the maxValue
    const clampedValue = Math.max(minValue, Math.min(maxValue, value));

    // Calculate the percentage position of the value within the range
    const position = ((clampedValue - minValue) / (maxValue - minValue)) * 100;

    if (position > 97)
      return 97;

    if (position < 3)
      return 3;

    return position;
  }

  generateLinearGradient(minValue: number, maxValue: number, averageValue: number, standardDeviation: number): string
  {
    // Calculate the key thresholds
    const greenStart = averageValue - standardDeviation; // Start of green area
    const greenEnd = averageValue + standardDeviation;   // End of green area

    const yellowStart1 = greenStart - 2*standardDeviation; // Start of first yellow area
    const yellowEnd1 = greenStart;                       // End of first yellow area
    const yellowStart2 = greenEnd;                       // Start of second yellow area
    const yellowEnd2 = greenEnd + 2*standardDeviation;     // End of second yellow area

    const redEnd1 = yellowStart1;                        // End of first red area
    const redStart2 = yellowEnd2;                        // Start of second red area

    // Calculate percentages for the gradient stops
    const percentage = (value) => ((value - minValue) / (maxValue - minValue)) * 100;

    const redEnd1Percent = percentage(redEnd1);
    const yellowStart1Percent = percentage(yellowStart1);
    const yellowEnd1Percent = percentage(yellowEnd1);
    const greenStartPercent = percentage(greenStart);
    const greenEndPercent = percentage(greenEnd);
    const yellowStart2Percent = percentage(yellowStart2);
    const yellowEnd2Percent = percentage(yellowEnd2);
    const redStart2Percent = percentage(redStart2);

    const redMiddle = (redEnd1Percent)/2;
    const yellowMiddle = yellowStart1Percent + (yellowEnd1Percent - redEnd1Percent)/2;
    const greenEnd1Middle = greenStartPercent + (greenEndPercent - yellowEnd1Percent)/2;
    const yellow2Middle = yellowStart2Percent+ (yellowEnd2Percent - greenEndPercent)/2;
    const red2Middle = redStart2Percent + (100 - yellowEnd2Percent) / 2;

    // Generate the CSS linear-gradient value
    return `linear-gradient(90deg, 
    var(--red) ${redMiddle}%, 
    var(--yellow) ${yellowMiddle}%, 
    var(--green) ${greenEnd1Middle}%, 
    var(--yellow) ${yellow2Middle}%, 
    var(--red) ${red2Middle}%
  )`;
  }

  
  showDiagram(diagram: GaitReportDiagrams)
  {
    const modalRef = this.modalService.open(RunningReportDiagramPopupComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.title = diagram.category + " - " + diagram.title;
    modalRef.componentInstance.url = diagram.url;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  getDiagram(category: string, title: string): GaitReportDiagrams  {
    return this.groupedDiagrams[category]?.find(diagrams => diagrams.title === title)
  }

  showHelp(key: string)
  {
    const modalRef = this.modalService.open(ExerciseToolTipModalComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.key = key;
    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  groupBy(xs, key)
  {
    return xs.reduce(function (rv, x)
    {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  getNumberFromNumber(value: string)
  {
    if (!value)
      return 0;

    return parseInt(value);
  }

  openHelpArticle(): void
  {
    window.open(
      'https://rundnahelp.zendesk.com/hc/en-us/articles/32083645986583-Running-Gait-Score-Interpretation',
      '_blank'
    );
  }

  ngOnDestroy(): void
  {
  }
}
